import type Phaser from 'phaser';

/** 일곱 죄악 각인 슬롯 ID (데이터·도감 연동용). */
export const IMPRINT_IDS = [
  'superbia',
  'invidia',
  'ira',
  'acedia',
  'avaritia',
  'gula',
  'luxuria',
] as const;

export type ImprintId = (typeof IMPRINT_IDS)[number];

export const HELL_DEPTH_MAX = 9;

/** 플레이어가 보유한 각인 ID 목록 */
export const REGISTRY_IMPRINTS_OWNED = 'progress.imprintsOwned';

export function getOwnedImprintIds(registry: Phaser.Data.DataManager): string[] {
  const raw = registry.get(REGISTRY_IMPRINTS_OWNED);
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

export function getOwnedImprintCount(registry: Phaser.Data.DataManager): number {
  return getOwnedImprintIds(registry).length;
}

/** `targetDepth`(지옥 거점 기준 층 번호)에 **처음 들어가기** 위해 필요한 각인 개수. */
export function requiredImprintsToEnterHellDepth(targetDepth: number): number {
  if (targetDepth <= 2) return 0;
  return Math.min(Math.max(0, targetDepth - 2), IMPRINT_IDS.length);
}

/** 현재 보유 각인으로 `targetDepth` 층 거점/포털에 진입 가능한지 */
export function canEnterHellDepth(registry: Phaser.Data.DataManager, targetDepth: number): boolean {
  return getOwnedImprintCount(registry) >= requiredImprintsToEnterHellDepth(targetDepth);
}

export function grantImprint(registry: Phaser.Data.DataManager, imprintId: ImprintId): boolean {
  if (!(IMPRINT_IDS as readonly string[]).includes(imprintId)) return false;
  const owned = getOwnedImprintIds(registry);
  if (owned.includes(imprintId)) return false;
  registry.set(REGISTRY_IMPRINTS_OWNED, [...owned, imprintId]);
  return true;
}

export function hasAllImprints(registry: Phaser.Data.DataManager): boolean {
  return getOwnedImprintCount(registry) >= IMPRINT_IDS.length;
}

/** 진입 불가 시 거점·포털 안내용 한 줄 */
export function hellDepthBlockedReason(registry: Phaser.Data.DataManager, targetDepth: number): string | null {
  if (canEnterHellDepth(registry, targetDepth)) return null;
  const need = requiredImprintsToEnterHellDepth(targetDepth);
  const have = getOwnedImprintCount(registry);
  return `제${targetDepth}층 진입에는 각인이 ${need}개 이상 필요합니다. (현재 ${have}개)`;
}

/** 각인 패널·도움말용 (거점 UI에서 그대로 사용). */
export const IMPRINT_PANEL_BODY_UNLOCKED =
  '【층 진입과 각인】\n' +
  '제3층 거점으로 내려가려면 각인을 최소 1개 이상 획득해야 합니다.\n' +
  '한 단계 더 깊은 층으로 갈 때마다, 그 다음 층 진입에 필요한 각인 수가 1개씩 늘어납니다.\n' +
  '제9층에서는 일곱 죄악 각인이 모두 개방되어 고르고 강화할 수 있습니다.\n\n' +
  '【요약】 제4층 진입: 각인 2개 이상 … 제9층 진입: 각인 7개(전 슬롯 해금).\n\n' +
  '던전에서의 실제 획득·장착은 이후 단계에서 연결합니다.';

export const IMPRINT_PANEL_BODY_LOCKED =
  '제2층 거점에 도달하면 장비·각인·상점·도감이 열립니다.\n\n' +
  '각인은 상위 층으로 내려갈수록 더 많이 필요해지며, 9층에서는 일곱 각인이 모두 열립니다.';
